<template>
    <ion-page>
        <ion-header :translucent="true"></ion-header>
        <ion-content :fullscreen="true">
            <div class="desktop-panel">
                <div class="aurora-container">
                    <AuroraAnimation />
                </div>
                <div class="auth-intro-header">
                    <p v-if="groupName">
                        {{ $t("auth.group-shared-auth.invited-for") }}
                        <br />
                        <span class="bold">{{ groupName }}</span>
                    </p>
                    <div v-else class="logo-container">
                        <img
                            class="logo"
                            alt="Sportspoule logo"
                            src="@/assets/images/sportspoule-logo.png"
                        />
                    </div>

                    <p v-if="description" style="margin-bottom: -100px">
                        {{ description }}
                    </p>
                </div>
                <div class="actions">
                    <sign-in-with-button driver="apple" />
                    <sign-in-with-button driver="google" />
                    <!-- <sign-in-with-button driver="facebook" /> -->
                    <or-divider />
                    <div class="btn white" @click="goToRegister">
                        {{ $t("auth.get-started.register") }}
                    </div>
                    <div class="btn transparant" @click="goToLogin">
                        {{ $t("auth.get-started.login") }}
                    </div>
                </div>
            </div>
        </ion-content>
    </ion-page>
</template>

<script lang="ts" setup>
    import { IonPage, IonContent, IonHeader } from "@ionic/vue";
    import OrDivider from "@/components/OrDivider.vue";
    import SignInWithButton from "@/components/SignInWithButton.vue";
    import { useRouter } from "vue-router";
    import { onMounted, ref } from "vue";
    import ApiService from "@/services/api.service";
    import StorageService from "@/services/storage.service";
    import GroupService from "@/services/group.service";
    import { ImpactStyle } from "@capacitor/haptics";
    import { safeHapticsImpact } from "@/utils/haptics";
    import AuroraAnimation from "@/components/animated/AuroraAnimation.vue";

    const router = useRouter();

    const groupName = ref(null);
    const description = ref<string | null>(null);

    const goToRegister = () => {
        safeHapticsImpact(ImpactStyle.Medium);
        router.push({ name: "auth.register" });
    };

    const goToLogin = () => {
        safeHapticsImpact(ImpactStyle.Medium);
        router.push({ name: "auth.login" });
    };

    const trackLoginStarted = async () => {
        const redirect = router.currentRoute.value.query.redirect;
        const redirectStr = typeof redirect === "string" ? redirect : null;
        const intent = redirectStr?.includes("checkout")
            ? "checkout"
            : "general";

        try {
            await ApiService.post("t", {
                type: "login.started",
                properties: {
                    path: "/get-started",
                    redirect: redirectStr,
                    intent,
                },
            });
        } catch {
            // tracking is best-effort
        }
    };

    onMounted(async () => {
        // Ensure a request is made so the visitor is logged (funnel analytics)
        try {
            await ApiService.get("profile");
        } catch (error: any) {
            // Handle 401 gracefully - user is not authenticated, which is expected on this page
            if (error.response?.status !== 401) {
                throw error;
            }
        }

        trackLoginStarted();

        if (typeof router.currentRoute.value.query.redirect === "string") {
            await StorageService.set(
                "intended_url",
                router.currentRoute.value.query.redirect,
            );
        }
        if (typeof router.currentRoute.value.query.description === "string") {
            description.value = router.currentRoute.value.query.description;
        }
        const intendedUrl: string = await StorageService.get("intended_url");
        if (intendedUrl?.includes("poules/share")) {
            const groupToken = intendedUrl.split("/").filter(Boolean).pop();
            if (groupToken) {
                try {
                    groupName.value =
                        await GroupService.getPouleNameByShareLink(groupToken);
                } catch (error: any) {
                    if (error.response?.status === 404) {
                        await StorageService.remove("intended_url");
                    }
                }
            }
        }
    });
</script>

<style lang="scss" scoped>
    @use "@/assets/scss/specific/auth" as *;

    .auth-intro-header {
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 110px 24px 0 24px;

        .logo {
            color: var(--color-white);
            fill: var(--color-white);
            margin-bottom: vw(40px);
            height: vw(120px);
        }

        p {
            font-size: 18px;
            font-weight: 400;
            line-height: 25.2px;
            text-align: center;
        }

        .bold {
            font-weight: 700;
        }
    }

    .actions {
        .btn {
            margin-bottom: 16px;

            &.google {
                color: var(--color-always-white);
            }
        }
    }
</style>
